import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { Badge, Button, ConfirmDialog, Input } from '@/components/ui'
import {
  CUSTOM_DOMAIN_QUERY_KEY,
  addCustomDomain,
  getCustomDomain,
  removeCustomDomain,
  verifyCustomDomain,
  type CustomDomain,
  type CustomDomainStatus,
} from '@/api/services/custom-domain.service'
import { useFeatureFlags } from '@/features/feature-flags/useFeatureFlags'
import { useAuthStore } from '@/stores/useAuthStore'
import { tenantHostLabel } from '@/lib/tenantHost'
import { FieldGroup, SettingsPane } from '../organization-ui'

const CUSTOM_DOMAIN_FEATURE = 'tenant.custom_domain'
const queryKey = CUSTOM_DOMAIN_QUERY_KEY

const STATUS: Record<
  CustomDomainStatus,
  { label: string; variant: 'success' | 'warning' | 'danger' }
> = {
  active: { label: 'Connected', variant: 'success' },
  pending: { label: 'Waiting for DNS', variant: 'warning' },
  failed: { label: 'Not verified', variant: 'danger' },
}

const apiMessage = (error: unknown, fallback: string): string =>
  (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? fallback

function CopyValue({ value }: { value: string }): React.ReactElement {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-left font-mono text-xs text-gray-800 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
      title="Copy"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
    >
      <span className="break-all">{value}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-green-600" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
      )}
    </button>
  )
}

function DnsRecords({ domain }: { domain: CustomDomain }): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-left text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          <tr>
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Name / host</th>
            <th className="px-3 py-2 font-medium">Value</th>
            <th className="px-3 py-2 font-medium">Check</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {domain.records.map((record) => {
            const passed =
              record.type === 'TXT' ? domain.lastCheck?.ownership : domain.lastCheck?.routing
            return (
              <tr key={record.type} className="align-top">
                <td className="px-3 py-2 font-mono text-xs">{record.type}</td>
                <td className="px-3 py-2">
                  <CopyValue value={record.name} />
                </td>
                <td className="px-3 py-2">
                  <CopyValue value={record.value} />
                </td>
                <td className="px-3 py-2 text-xs">
                  {domain.status === 'active' || passed ? (
                    <span className="text-green-700 dark:text-green-400">Found</span>
                  ) : domain.lastCheckedAt ? (
                    <span className="text-amber-700 dark:text-amber-400">Not found yet</span>
                  ) : (
                    <span className="text-gray-500">Not checked</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The workspace's own domain (hr.acme.com). Self-serve: the admin adds the
 * domain, copies two DNS records to their provider, and it goes live once
 * both are seen. Growth and Enterprise; removing stays open on every plan so a
 * downgraded workspace can still disconnect.
 */
export const DomainPane: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const queryClient = useQueryClient()
  const { flags, isLoading: flagsLoading } = useFeatureFlags()
  const allowed = flags[CUSTOM_DOMAIN_FEATURE] === true
  const slug = useAuthStore((s) => s.user?.organizationSlug)
  const [hostname, setHostname] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(false)

  const { data, isPending } = useQuery({ queryKey, queryFn: getCustomDomain })
  const setDomain = (domain: CustomDomain | null): void => {
    queryClient.setQueryData(queryKey, (current: typeof data) =>
      current ? { ...current, domain } : current
    )
  }

  const add = useMutation({
    mutationFn: addCustomDomain,
    onSuccess: (domain) => {
      setDomain(domain)
      setHostname('')
      toast.success('Domain added. Now add the two DNS records below.')
    },
    onError: (error) => toast.error(apiMessage(error, 'The domain could not be added.')),
  })
  const verify = useMutation({
    mutationFn: verifyCustomDomain,
    onSuccess: (domain) => {
      setDomain(domain)
      if (domain.status === 'active') toast.success(`${domain.hostname} is connected.`)
      else toast.info(domain.lastCheck?.error ?? 'DNS records not found yet.')
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not check DNS. Please try again.')),
  })
  const remove = useMutation({
    mutationFn: removeCustomDomain,
    onSuccess: () => {
      setDomain(null)
      setConfirmRemove(false)
      toast.success('Custom domain removed.')
    },
    onError: (error) => toast.error(apiMessage(error, 'The domain could not be removed.')),
  })

  const domain = data?.domain ?? null
  const subdomain = slug ? tenantHostLabel(slug) : null

  return (
    <SettingsPane
      title="Custom domain"
      description="Serve your workspace from your own domain, such as hr.yourcompany.com."
    >
      {subdomain && (
        <FieldGroup label="KAAMCO address">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Your workspace is always available at{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{subdomain}</span>
            {domain?.status === 'active' ? ', alongside your custom domain.' : '.'}
          </p>
        </FieldGroup>
      )}

      <FieldGroup label="Your domain">
        {isPending || flagsLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !data?.target ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Custom domains aren&apos;t available yet. Check back soon.
          </p>
        ) : domain ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {domain.hostname}
              </span>
              <Badge variant={STATUS[domain.status].variant}>{STATUS[domain.status].label}</Badge>
              {domain.status === 'active' && (
                <a
                  href={`https://${domain.hostname}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  Open <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              )}
            </div>

            {domain.status === 'active' ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Your team can sign in at{' '}
                <span className="font-medium">https://{domain.hostname}</span>. Invitations and
                email links now use this address. The first visit can take a minute while the
                security certificate is issued. Keep both DNS records in place.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Add these two records at your DNS provider (GoDaddy, Namecheap, Cloudflare, Route
                  53…). Changes can take up to an hour to show. We check automatically every 10
                  minutes for 7 days.
                  {domain.status === 'failed' &&
                    ' Automatic checks have stopped; use "Check now" once the records are in place.'}
                </p>
                {domain.lastCheck?.error && domain.lastCheckedAt && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                    Last check: {domain.lastCheck.error}
                  </p>
                )}
              </>
            )}

            <DnsRecords domain={domain} />
            <p className="text-xs text-gray-500">
              Using your root domain (yourcompany.com) instead of a subdomain? Root domains
              can&apos;t use CNAME at most providers: add A records with the same addresses as{' '}
              <span className="font-mono">{data.target}</span> instead.
            </p>

            <div className="flex flex-wrap gap-2">
              {domain.status !== 'active' && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  loading={verify.isPending}
                  disabled={disabled || !allowed}
                  onClick={() => verify.mutate()}
                >
                  Check now
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={() => setConfirmRemove(true)}
              >
                Remove domain
              </Button>
            </div>
          </div>
        ) : !allowed ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
            Custom domains are included in the Growth and Enterprise plans.{' '}
            <Link to="/billing" className="font-medium text-primary hover:underline">
              See plans
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="flex-1">
              <Input
                label="Domain"
                placeholder="hr.yourcompany.com"
                value={hostname}
                disabled={disabled}
                autoCapitalize="off"
                spellCheck={false}
                onChange={(event) => setHostname(event.target.value)}
                onKeyDown={(event) => {
                  // Inside the settings form: Enter must not submit it.
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    if (hostname.trim()) add.mutate(hostname.trim())
                  }
                }}
                hint="A subdomain you own works best, like hr. or portal."
              />
            </div>
            <Button
              type="button"
              variant="primary"
              className="sm:mt-6"
              loading={add.isPending}
              disabled={disabled || !hostname.trim()}
              onClick={() => add.mutate(hostname.trim())}
            >
              Connect domain
            </Button>
          </div>
        )}
      </FieldGroup>

      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => remove.mutate()}
        title="Remove custom domain?"
        message={`${domain?.hostname ?? 'The domain'} will stop serving your workspace right away. Links to it will stop working; your KAAMCO address keeps working.`}
        confirmLabel="Remove domain"
        variant="danger"
        confirmLoading={remove.isPending}
      />
    </SettingsPane>
  )
}
